import React, { useState } from 'react';
import { Info } from 'lucide-react';
import type { AllClassStageSummary } from '../services/kvsResultApi';
import { ScoreBandInfoDialog, ScoreBandStage } from './ScoreBandInfoDialog';

/**
 * Percent of `total`, unrounded. Rates are rounded once, at display: the API's
 * excellentRate/supportRate are already rounded to 2 dp, and rounding those again
 * shows e.g. 30.95 → "30.9%" where the stage dashboard (from counts) shows 31.0%.
 */
export const shareOf = (count: number, total: number): number => (total > 0 ? (count / total) * 100 : 0);

/**
 * Score bands of the stage cards. The thresholds are the backend's (kvsresult
 * summaries: excellentStudents > 90%, needSupport < 50%); everyone else is between.
 */
export const SCORE_BANDS = [
  { key: 'excellent', label: 'Excellent', range: 'score above 90%', color: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  { key: 'moderate', label: 'Moderate', range: 'score 50–90%', color: 'bg-blue-400', chip: 'bg-blue-50 text-blue-700 ring-blue-200' },
  { key: 'support', label: 'Needs Support', range: 'score below 50%', color: 'bg-rose-400', chip: 'bg-rose-50 text-rose-700 ring-rose-200' },
] as const;

type BandCounts = Pick<AllClassStageSummary, 'totalStudents' | 'excellentStudents' | 'needSupport'>;

/**
 * Students by score band — a stacked bar plus a legend with each band's range and
 * share of students. Shared by the Stage Selection cards and the stage dashboards,
 * so they always draw the same figures the same way. With `stage`, a "How it's
 * calculated" button opens that stage's explanation (`foundationTerm`: which HPC
 * term the Foundational figures are from; default Term 2).
 */
export const ScoreBandPanel: React.FC<{
  stats: BandCounts;
  stage?: ScoreBandStage;
  foundationTerm?: 1 | 2;
  className?: string;
}> = ({ stats, stage, foundationTerm, className = 'mt-3' }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { totalStudents, excellentStudents, needSupport } = stats;
  const counts = {
    excellent: excellentStudents,
    moderate: Math.max(0, totalStudents - excellentStudents - needSupport),
    support: needSupport,
  };
  const bands = SCORE_BANDS.map((band) => ({
    ...band,
    count: counts[band.key],
    pct: shareOf(counts[band.key], totalStudents),
  }));

  return (
    <div className={`space-y-2 rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 ${className}`}>
      <div className="flex flex-wrap justify-between items-center gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">
            Students by Score Band
          </span>
          {stage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setInfoOpen(true);
              }}
              aria-haspopup="dialog"
              className="inline-flex items-center gap-1 rounded-md px-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            >
              <Info size={13} aria-hidden="true" /> How it&apos;s calculated
            </button>
          )}
        </div>
        <span className="text-[10px] font-medium text-gray-600">% of students</span>
      </div>
      <div
        className="flex h-2 w-full rounded-full overflow-hidden bg-gray-200"
        role="img"
        aria-label={bands.map((b) => `${b.label} (${b.range}): ${b.pct.toFixed(1)}% of students`).join(', ')}
      >
        {bands.map((b) => (
          <div
            key={b.key}
            className={b.color}
            style={{ width: `${b.pct}%` }}
            title={`${b.label} (${b.range}): ${b.pct.toFixed(1)}% · ${b.count.toLocaleString()} students`}
          />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {bands.map((b, i) => (
          <div key={b.key} className={i === 0 ? 'text-left' : i === 1 ? 'text-center' : 'text-right'}>
            {/* On a narrow card the % drops to its own line; on the narrowest (phones, ~768px
                windows) the label wraps too rather than run into its neighbour, its dot kept
                beside the first word. */}
            <p
              className={`flex flex-wrap items-center gap-x-1.5 text-[11px] font-semibold text-gray-700 ${
                i === 0 ? 'justify-start' : i === 1 ? 'justify-center' : 'justify-end'
              }`}
            >
              <span>
                <span className={`inline-block h-2 w-2 mr-1.5 rounded-full align-middle ${b.color}`} aria-hidden="true" />
                {b.label}
              </span>
              <span className="whitespace-nowrap">{b.pct.toFixed(1)}%</span>
            </p>
            {/* The cut-off, as a tag in the band's colour so it reads at a glance. */}
            <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${b.chip}`}>
              {b.range}
            </span>
          </div>
        ))}
      </div>
      {infoOpen && stage && (
        <ScoreBandInfoDialog
          stage={stage}
          foundationTerm={foundationTerm}
          bands={bands}
          totalStudents={totalStudents}
          onClose={() => setInfoOpen(false)}
        />
      )}
    </div>
  );
};

/**
 * The stage card's score-band panel as a card at the top of a stage dashboard, with
 * a short explanation (children) on the left. `matchesStageCard` is false once the
 * dashboard's own filters (class, term) move it off what the Stage Selection card shows.
 */
export const StageScoreBandCard: React.FC<{
  stats: BandCounts;
  stage: ScoreBandStage;
  foundationTerm?: 1 | 2;
  matchesStageCard: boolean;
  children: React.ReactNode;
}> = ({ stats, stage, foundationTerm, matchesStageCard, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-4">
    <div className="lg:w-96 shrink-0 text-[13px] text-gray-600 space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
        {matchesStageCard ? 'Same as the Stage Selection card' : 'For the filters selected above'}
      </p>
      {children}
    </div>
    <ScoreBandPanel stats={stats} stage={stage} foundationTerm={foundationTerm} className="flex-1" />
  </div>
);
